import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@monksflow/monks-ui';
import { MoreVerticalIcon } from 'lucide-react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';

import { ROUTES } from '@/shared/config/routes';

import type { ToolRowData } from '../ToolsTable/ToolsTable.types';

interface ToolRowActionsProps {
  tool: ToolRowData;
  /** The row genuinely owns tools that take configuration. */
  hasConfigurable: boolean;
  isManaged: boolean;
  isExternalToolsAgent: boolean;
  onManageWorkspaces: (tool: ToolRowData) => void;
  onEditAgent?: (tool: ToolRowData) => void;
  onDeleteAgent?: (tool: ToolRowData) => void;
}

export function ToolRowActions({
  tool,
  hasConfigurable,
  isManaged,
  isExternalToolsAgent,
  onManageWorkspaces,
  onEditAgent,
  onDeleteAgent,
}: ToolRowActionsProps) {
  const { t } = useTranslation('admin');
  // No admin key names a row's kebab yet; see the folder's report. Borrowed rather than
  // invented, because three locale catalogues have to move together to add one.
  const navigate = useNavigate();

  // Every system agent has a config page (at least its Basic Information block), and the
  // external-tools agent has its own.
  const isSystemAgent = tool.source !== 'user';
  const canConfigure = hasConfigurable || isSystemAgent || isExternalToolsAgent;

  const isCustomTool = tool.source === 'user';
  // Who can use a built agent is its scope, set in the builder; the workspace sheet would write
  // an access list the agent never reads. Only the builder edits or deletes a custom agent, and it
  // opens built ones alone: a legacy agent the import could not move has neither.
  const isBuiltAgent = tool.builtAgent !== undefined;
  // Without the builder's edit and delete, a built agent has nothing here to offer.
  if (isBuiltAgent && !(isCustomTool && (onEditAgent || onDeleteAgent))) return null;

  const configureItem = (
    <DropdownMenuItem
      disabled={!canConfigure}
      onSelect={() => {
        void navigate(ROUTES.ADMIN_AGENT_CONFIG(tool.id));
      }}
    >
      {t('admin.toolsTable.configureTool')}
    </DropdownMenuItem>
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="mini"
          aria-label={t('admin.toolsTable.moreActions')}
          className="size-9 p-0"
        >
          <MoreVerticalIcon className="size-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {!isBuiltAgent && (
          <DropdownMenuItem
            onSelect={() => {
              onManageWorkspaces(tool);
            }}
          >
            {t('admin.toolsTable.manageWorkspaces')}
          </DropdownMenuItem>
        )}

        {!isManaged &&
          !isBuiltAgent &&
          (canConfigure ? (
            configureItem
          ) : (
            <Tooltip>
              {/* The wrapper keeps the tooltip reachable while the item itself is inert. */}
              <TooltipTrigger asChild>
                <span>{configureItem}</span>
              </TooltipTrigger>
              <TooltipContent side="left">
                {t('admin.toolsTable.thisToolDoesnTHave')}
              </TooltipContent>
            </Tooltip>
          ))}

        {isCustomTool && isBuiltAgent && onEditAgent && (
          <DropdownMenuItem
            onSelect={() => {
              onEditAgent(tool);
            }}
          >
            {t('admin.toolsTable.editTool')}
          </DropdownMenuItem>
        )}

        {isCustomTool && isBuiltAgent && !tool.builtAgent?.archived && onDeleteAgent && (
          <DropdownMenuItem
            variant="destructive"
            onSelect={() => {
              onDeleteAgent(tool);
            }}
          >
            {t('admin.toolsTable.deleteTool')}
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
